/**
 * Laboratorio de juego: interfaz deliberadamente fea y observable.
 *
 * No decide **nada**. Pinta el ultimo snapshot, recoge selecciones y manda
 * acciones. Toda la legalidad la resuelve el servidor, y lo unico que esta
 * pantalla hace con un rechazo es enseñarlo tal cual llego.
 */

import { useState, type ReactElement } from 'react';

import {
  EMPTY_SELECTION,
  buildAction,
  describeMissing,
  type Selection,
  type SlotRef,
  type SupportedAction,
} from './selection';
import {
  isMyTurn,
  toAltarRows,
  toCardRow,
  toDiscardRows,
  toLineRows,
  type SlotRow,
} from './presentation';
import { useGameRoom } from './useGameRoom';
import type { InstanceId, MatchView } from '@tcg/game-rules';

function Slot({
  slot,
  selectedCard,
  selectedSlot,
  onCard,
  onSlot,
}: {
  slot: SlotRow;
  selectedCard: InstanceId | null;
  selectedSlot: SlotRef | null;
  onCard?: (id: InstanceId) => void;
  onSlot?: (ref: SlotRef) => void;
}): ReactElement {
  const occupant = slot.occupant;
  const slotSelected =
    selectedSlot !== null && selectedSlot.line === slot.line && selectedSlot.index === slot.index;

  if (occupant === null) {
    return (
      <button
        type="button"
        className={`slot slot-empty${slotSelected ? ' selected' : ''}`}
        onClick={
          onSlot === undefined ? undefined : () => onSlot({ line: slot.line, index: slot.index })
        }
        disabled={onSlot === undefined}
        data-testid={`slot-${slot.line}-${String(slot.index)}`}
      >
        <span className="slot-index">{slot.index + 1}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`slot${occupant.instanceId === selectedCard ? ' selected' : ''}`}
      onClick={onCard === undefined ? undefined : () => onCard(occupant.instanceId)}
      disabled={onCard === undefined}
      data-testid={`slot-${slot.line}-${String(slot.index)}`}
    >
      <strong>{occupant.name}</strong>
      {occupant.kind === 'invocation' ? (
        <>
          <span>
            P {occupant.power ?? '?'} / V {occupant.currentLife ?? '?'}
            {occupant.maxLife === null ? '' : `/${String(occupant.maxLife)}`} / A{' '}
            {occupant.range ?? '?'}
          </span>
          <span className="muted">{occupant.orientation}</span>
        </>
      ) : (
        <span className="muted">Hechizo</span>
      )}
    </button>
  );
}

function Line({
  label,
  slots,
  selectedCard,
  selectedSlot,
  onCard,
  onSlot,
}: {
  label: string;
  slots: readonly SlotRow[];
  selectedCard: InstanceId | null;
  selectedSlot: SlotRef | null;
  onCard?: (id: InstanceId) => void;
  onSlot?: (ref: SlotRef) => void;
}): ReactElement {
  return (
    <div className="line">
      <span className="line-label">{label}</span>
      <div className="line-slots">
        {slots.map((slot) => (
          <Slot
            key={`${slot.line}-${String(slot.index)}`}
            slot={slot}
            selectedCard={selectedCard}
            selectedSlot={selectedSlot}
            onCard={onCard}
            onSlot={onSlot}
          />
        ))}
      </div>
    </div>
  );
}

function Board({
  view,
  selection,
  select,
}: {
  view: MatchView;
  selection: Selection;
  select: (patch: Partial<Selection>) => void;
}): ReactElement {
  const enemy = view.opponent.board.field;
  const mine = view.own.board.field;

  return (
    <section className="board">
      <h3>Rival · {view.opponent.seat}</h3>
      <Line
        label="DORSAL"
        slots={toLineRows(enemy.dorsal, 'dorsal')}
        selectedCard={selection.enemyCard}
        selectedSlot={null}
        onCard={(id) => {
          select({ enemyCard: id });
        }}
      />
      <Line
        label="FRONT"
        slots={toLineRows(enemy.front, 'front')}
        selectedCard={selection.enemyCard}
        selectedSlot={null}
        onCard={(id) => {
          select({ enemyCard: id });
        }}
      />

      <hr />

      <Line
        label="FRONT"
        slots={toLineRows(mine.front, 'front')}
        selectedCard={selection.fieldCard}
        selectedSlot={selection.slot}
        onCard={(id) => {
          select({ fieldCard: id });
        }}
        onSlot={(ref) => {
          select({ slot: ref });
        }}
      />
      <Line
        label="DORSAL"
        slots={toLineRows(mine.dorsal, 'dorsal')}
        selectedCard={selection.fieldCard}
        selectedSlot={selection.slot}
        onCard={(id) => {
          select({ fieldCard: id });
        }}
        onSlot={(ref) => {
          select({ slot: ref });
        }}
      />
      <h3>Tú · {view.own.seat}</h3>
    </section>
  );
}

const ACTION_LABEL: Record<SupportedAction, string> = {
  playOffer: 'Jugar Ofrenda',
  exhaustOffer: 'Agotar',
  playInvocation: 'Jugar Invocación',
  moveInvocation: 'Mover',
  attackCard: 'Atacar',
  attackPlayer: 'Ataque directo',
  endTurn: 'Terminar turno',
};

const ACTION_ORDER: readonly SupportedAction[] = [
  'playOffer',
  'exhaustOffer',
  'playInvocation',
  'moveInvocation',
  'attackCard',
  'attackPlayer',
  'endTurn',
];

/** Pantalla completa del laboratorio. */
export function GameLab(): ReactElement {
  const { state, send, retry } = useGameRoom();
  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION);

  const select = (patch: Partial<Selection>): void => {
    setSelection((current) => ({ ...current, ...patch }));
  };

  if (state.phase === 'error') {
    return (
      <main className="shell">
        <h1 className="title">Game Lab</h1>
        <p className="error" data-testid="connection-error">
          {state.error ?? 'Error de conexión.'}
        </p>
        <p className="muted">
          Comprueba que el servidor está levantado (<code>pnpm --filter @tcg/server dev</code>) y
          vuelve a intentarlo. No hace falta recargar la página.
        </p>
        <button type="button" className="action" onClick={retry} data-testid="retry">
          Reintentar
        </button>
        <section className="log">
          <h2>Eventos</h2>
          <ol>
            {state.log.map((entry, index) => (
              <li key={`${String(index)}-${entry}`}>{entry}</li>
            ))}
          </ol>
        </section>
      </main>
    );
  }

  if (state.view === null) {
    return (
      <main className="shell">
        <h1 className="title">Game Lab</h1>
        <p data-testid="waiting">
          {state.phase === 'connecting'
            ? 'Conectando con el servidor…'
            : 'Esperando al segundo jugador…'}
        </p>
        {state.roomId === null ? null : <p className="muted">Sala: {state.roomId}</p>}
        <button type="button" className="action" onClick={retry} data-testid="retry">
          Reintentar
        </button>
      </main>
    );
  }

  const view = state.view;
  const myTurn = isMyTurn(view);

  return (
    <main className="shell lab">
      <header>
        <h1 className="title">Game Lab</h1>
        <p data-testid="status-line">
          Conectado · Asiento: <strong>{view.viewer}</strong> · Turno:{' '}
          <strong data-testid="active-seat">{view.turn.activeSeat}</strong> ·{' '}
          {myTurn ? 'te toca' : 'espera'}
        </p>
        {state.roomId === null ? null : <p className="muted">Sala: {state.roomId}</p>}
      </header>

      <section className="summary" data-testid="opponent-summary">
        <h2>Rival</h2>
        <p>
          {view.opponent.life} PV · Mano:{' '}
          <span data-testid="opponent-hand-count">{view.opponent.handCount}</span> · Mazo:{' '}
          {view.opponent.deckCount}
        </p>
        <p className="muted">
          Descarte rival:{' '}
          {toDiscardRows(view.opponent.discard)
            .map((card) => card.name)
            .join(', ') || '(vacío)'}
        </p>
        <p className="muted">
          Altar rival:{' '}
          {toAltarRows(view.opponent.board.altar.offers)
            .map((offer) => `${offer.name} (${offer.state})`)
            .join(', ') || '(vacío)'}
        </p>
      </section>

      <Board view={view} selection={selection} select={select} />

      <section className="summary" data-testid="own-summary">
        <h2>Tú</h2>
        <p>
          {view.own.life} PV · Recurso: <strong>{view.turn.temporaryResource}</strong> · Mazo:{' '}
          {view.own.deckCount}
        </p>
        <p className="muted">
          Descarte propio:{' '}
          {toDiscardRows(view.own.discard)
            .map((card) => card.name)
            .join(', ') || '(vacío)'}
        </p>
      </section>

      <section className="altar">
        <h2>Tu Altar</h2>
        <ul>
          {toAltarRows(view.own.board.altar.offers).map((offer) => (
            <li key={offer.instanceId}>
              <button
                type="button"
                className={selection.altarOffer === offer.instanceId ? 'selected' : ''}
                onClick={() => {
                  select({ altarOffer: offer.instanceId });
                }}
              >
                {offer.name} · Valor {offer.value ?? '?'} · {offer.state}
              </button>
            </li>
          ))}
          {view.own.board.altar.offers.length === 0 ? <li className="muted">(vacío)</li> : null}
        </ul>
      </section>

      <section className="hand">
        <h2>Tu mano ({view.own.hand.length})</h2>
        <ul>
          {view.own.hand.map(toCardRow).map((card) => (
            <li key={card.instanceId}>
              <button
                type="button"
                className={selection.handCard === card.instanceId ? 'selected' : ''}
                onClick={() => {
                  select({ handCard: card.instanceId });
                }}
                data-testid={`hand-${card.instanceId}`}
              >
                <strong>{card.name}</strong> · {card.type} · {card.costLabel}
                {card.stats === null ? '' : ` · ${card.stats}`}
                {card.unsupported ? ' · No implementado todavía' : ''}
                {card.effectText === null ? null : <em className="effect">{card.effectText}</em>}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="actions">
        {ACTION_ORDER.map((kind) => {
          const action = buildAction(kind, selection);

          return (
            <button
              key={kind}
              type="button"
              className="action"
              disabled={action === null}
              title={describeMissing(kind, selection) ?? ''}
              onClick={() => {
                if (action !== null) {
                  send(action);
                  setSelection(EMPTY_SELECTION);
                }
              }}
              data-testid={`action-${kind}`}
            >
              {ACTION_LABEL[kind]}
            </button>
          );
        })}
      </section>

      {state.rejection === null ? null : (
        <section className="rejection" data-testid="rejection">
          <strong data-testid="rejection-code">{state.rejection.code}</strong>
          <p>{state.rejection.message}</p>
        </section>
      )}

      <section className="log">
        <h2>Eventos</h2>
        <ol>
          {state.log.map((entry, index) => (
            <li key={`${String(index)}-${entry}`}>{entry}</li>
          ))}
        </ol>
      </section>
    </main>
  );
}
